<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">
          {{ title }}
        </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <v-tooltip location="start">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="cancel">
                <v-icon>mdi-close-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>Fermer</span>
          </v-tooltip>

          <v-tooltip location="start">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="save">
                <v-icon>mdi-content-save-all</v-icon>
              </v-btn>
            </template>
            <span>Sauvegarder</span>
          </v-tooltip>

          <v-tooltip location="start" v-if="canRemove">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon variant="text" @click="remove">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>Supprimer</span>
          </v-tooltip>
        </div>
      </v-toolbar>

      <v-card-text>
        <v-container fluid>
          <v-row density="compact">
            <v-col cols="12" md="6">
              <v-text-field v-model="panne.name" hint="Le sujet de la panne">
                <template #label> Sujet <span class="text-red">*</span> </template>
              </v-text-field>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field v-model="panne.creationDate" type="date" label="Date" readonly>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row density="compact">
            <v-col cols="12">
              <v-textarea v-model="panne.desc" hint="Observation à propos de la panne">
                <template #label> Observation <span class="text-red">*</span> </template>
              </v-textarea>
            </v-col>
          </v-row>

          <v-row density="compact">
            <v-col cols="12" md="6">
              <v-checkbox v-model="panne.createTask" label="Créer une activité de panne">
              </v-checkbox>
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox v-model="panne.repaired" label="Panne réparée" readonly>
              </v-checkbox>
            </v-col>
          </v-row>

        </v-container>
      </v-card-text>
    </v-card>

    <ConfirmDialog v-model="removePanneDialog" dialogTitle="Suppression" dialogText="Voulez-vous supprimer cette panne?"
      @confirm="onConfirm" @cancel="onCancel" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import { Panne } from "@/types";
import { deletePanne, getPanneById, postPanne, putPanne } from "@/utils/panne-api";

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();

const removePanneDialog = ref(false);
const loading = ref(false);

const title = ref("");
const panne = ref<Panne>({ createTask: false } as Panne);

const canRemove = computed(() => !!panne.value.id);

async function save() {
  loading.value = true;
  try {
    if (!panne.value.id) {
      const productId = route.params.productId as string;
      panne.value = await postPanne(panne.value, productId);

      router.replace({
        name: "panne",
        params: { productId, id: panne.value.id },
      });

      title.value = "Editer la panne";
      appStore.sendSuccessNotice("New panne has been added.");
    } else {
      await putPanne(panne.value);
      appStore.sendSuccessNotice("Panne has been updated.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function cancel() {
  router.push({
    name: "product",
    params: { id: route.params.productId },
  });
}

function remove() {
  removePanneDialog.value = true;
}

async function onConfirm() {
  loading.value = true;
  try {
    await deletePanne(panne.value.id!);
    cancel();
    appStore.sendSuccessNotice("Panne has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    removePanneDialog.value = false;
    loading.value = false;
  }
}

function onCancel() {
  removePanneDialog.value = false;
}

function isCreatingNewPanne(): boolean {
  return route.name === "newpanne" || route.params.id === "new";
}

onMounted(async () => {
  if (isCreatingNewPanne()) {
    title.value = "Créer une panne";
    panne.value = { createTask: false } as Panne;
    return;
  }

  title.value = "Editer la panne";
  panne.value = await getPanneById(route.params.id as string);
});
</script>
